import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Utility function to extract error message from API response
const extractErrorMessage = (error, defaultMessage = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const firstError = detail[0];
    return firstError?.msg || firstError?.message || defaultMessage;
  } else if (typeof detail === 'object' && detail !== null) {
    return detail.msg || detail.message || defaultMessage;
  } else if (typeof detail === 'string') {
    return detail;
  }
  return error?.message || defaultMessage;
};

// Auth Context
export const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading = true
  const [isInitialized, setIsInitialized] = useState(false);

  // Verify token and get fresh user data
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      console.log('No token found, user not authenticated');
      setUser(null);
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    try {
      console.log('Verifying token...');
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Token is valid, update user data with fresh data from server
      const userData = response.data;
      console.log('Token valid, user:', userData.email);
      
      localStorage.setItem('user_data', JSON.stringify(userData));
      setUser(userData);
      
    } catch (error) {
      console.error('Token verification failed:', error.response?.status);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        // Token is invalid or expired
        console.log('Token expired or invalid, clearing session');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_data');
        setUser(null);
      } else {
        // Network error or server error - try to use cached data
        console.log('Network error, trying cached data');
        try {
          const cachedData = localStorage.getItem('user_data');
          if (cachedData) {
            const parsed = JSON.parse(cachedData);
            if (parsed && parsed.email) {
              setUser(parsed);
            }
          }
        } catch (e) {
          localStorage.removeItem('user_data');
          localStorage.removeItem('access_token');
          setUser(null);
        }
      }
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  }, []);

  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Setup axios interceptors
  useEffect(() => {
    // Request interceptor to add auth token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle auth errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('401 received, clearing session');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_data');
          setUser(null);
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Auto-save functionality
  const autoSave = () => {
    console.log('Auto-saving user data...');
  };

  const logout = () => {
    autoSave();
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_data');
    setUser(null);
    console.log('User logged out');
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user_data', JSON.stringify(userData));
      
      setUser(userData);
      console.log('Login successful:', userData.email);
      
      return { success: true, user: userData };
    } catch (error) {
      return { 
        success: false, 
        error: extractErrorMessage(error, 'Erreur de connexion')
      };
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword
      });
      
      // Update first login status
      const updatedUser = { ...user, is_first_login: false };
      setUser(updatedUser);
      localStorage.setItem('user_data', JSON.stringify(updatedUser));
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: extractErrorMessage(error, 'Erreur lors du changement de mot de passe')
      };
    }
  };

  // Refresh user data from server
  const refreshUser = async () => {
    await verifyToken();
  };

  const value = {
    user,
    isLoading,
    isInitialized,
    login,
    logout,
    changePassword,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
